import { Elements, useStripe } from "@stripe/react-stripe-js";
import type { StripeExpressCheckoutElementClickEvent } from "@stripe/stripe-js";
import { useFormContext, useWatch } from "react-hook-form";
import { t } from "ttag";

import type { ContactInfoFormValues } from "@/features/onboarding/hooks";
import { useOnboardingUser } from "@/features/onboarding/hooks";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { ApplePayButton } from "@/features/payments/components";
import { calculateDiscountedTotal } from "@/features/products/utils";
import { PAYMENT_METHOD_TYPE } from "@/shared/domains";
import { CURRENCY } from "@/shared/models";
import { Button } from "@/shared/ui";

interface PayButtonProps {
  disabled?: boolean;
  label?: string;
  onClick?: VoidFunction;
}

export const PayButton = ({ disabled, label, onClick }: PayButtonProps) => {
  const stripe = useStripe();

  const selectedCategories = useOnboardingStore(
    (state) => state.selectedCategories,
  );
  const checkoutDiscount = useOnboardingStore(
    (state) => state.checkoutDiscount,
  );
  const metadata = useOnboardingStore((state) => state.urlParams);

  const user = useOnboardingUser();

  const selectedPrices = selectedCategories
    .map((cat) => cat.selectedProduct?.selectedPrice)
    .filter((price) => price !== undefined);

  const price = selectedPrices[0];

  const priceDiscountCode = selectedPrices.find(
    (price) => price.discount?.isValid,
  )?.discount?.code;

  const couponCode = checkoutDiscount?.code ?? priceDiscountCode;

  const form = useFormContext<ContactInfoFormValues>();

  const paymentMethodType = useWatch({
    control: form.control,
    name: "paymentMethodType",
  });

  const totalInCents = checkoutDiscount?.isValid
    ? checkoutDiscount.totalAfterDiscount * 100
    : calculateDiscountedTotal(selectedCategories) * 100;

  const handleExpressCheckoutClick = async (
    evt: StripeExpressCheckoutElementClickEvent,
  ) => {
    const isValid = await form.trigger();

    if (isValid) {
      onClick?.();
      evt.resolve();
    }
  };

  if (!price || !user) {
    return null;
  }

  return (
    <Elements
      stripe={stripe}
      options={{
        mode: "subscription",
        amount: Math.round(totalInCents),
        currency: CURRENCY.USD,
        appearance: {
          variables: {
            borderRadius: "8px",
          },
        },
      }}
    >
      {paymentMethodType === PAYMENT_METHOD_TYPE.APPLE_PAY ? (
        <ApplePayButton
          price={price}
          user={user}
          discount={couponCode}
          metadata={metadata}
          onClick={handleExpressCheckoutClick}
        />
      ) : (
        <Button size="lg" disabled={disabled} type="submit" className="w-full">
          {label ?? t`Complete Purchase`}
        </Button>
      )}
    </Elements>
  );
};
